import { Helmet } from "react-helmet";
import AnimalGrid from "components/animalgrid";
import BodyText from "components/bodytext";
import ContentBox from "components/contentbox";
import HeaderText from "components/headertext";
import PageGallery from "components/pagegallery";
import Image from "components/image";
import femPine5 from "img/blackpines/Nightshade/2022-07-23-DSC_6815.jpg";
import husbandryImg from "img/blackpines/Black Pine Snakes.png";
import collectionMeta from "content/collections/black-pinesnakes.json";
import { animalGridFromJson } from "hooks/useGallery";

const animalGridData = animalGridFromJson(collectionMeta);

const BlackPinesnake = () => {
  return (
    <ContentBox>
      <Helmet>
        <title>{collectionMeta.commonName}</title>
        <meta
          name="description"
          content="See the Black Pine Snakes (Pituophis melanoleucus lodingi)
            that are being kept here and learn more about their captive husbandry."
        />
        <link rel="canonical" href="https://www.gcreptiles.com/black-pinesnakes" />
      </Helmet>

      <HeaderText
        title={collectionMeta.commonName}
        subtitle={collectionMeta.scientificName}
      />

      <AnimalGrid data={animalGridData} />

      <BodyText>
        I got my black pine snakes as yearlings in 2022 from Summer Sutherland 
        at The Snake Keeper (TSK). TSK got theirs from Envy Reptiles in Springville, 
        Utah. Black pine snakes are listed as Threatened by the USFWS, so you 
        need an interstate commerce permit to sell or move them between states.
      </BodyText>

      <BodyText>
        Generally, all black pine snakes look the same. I have seen
        some black pines that retain more brown tones in their coloring than others,
        but mine are basically jet black.
        When I first got my pair, the only way I could tell them apart was that
        the female has a small white patch of scales on her chin.
      </BodyText>

      <BodyText>
        As far as temperament goes, my black pines couldn't be any gentler.
        The female has gotten much bigger than the male and I hand her off
        to visitors who are afraid of snakes without any concerns. On a few
        occasions people have sat down on the couch in my snake room and the
        female has sat still on their lap for half an hour or more.
      </BodyText>

      <Image
        src={femPine5}
        caption="July 2022, female black pine snake"
      />

      <h1>Husbandry</h1>

      <Image
        src={husbandryImg}
      />
      
      <BodyText>
        <h3>Temperatures, Humidity, and Light</h3>
        As you can see from the picture above, for most of the year I keep my black 
        pine snakes in
        enclosures where the daytime high (DTH) is about 79-82F for 12 hours a day, 
        and then at night, the nighttime low (NTL) is about 70F.
        The enclosures have higher than ambient humidity because I live in a 
        dry area where the relative humidity is frequently around 30%. So I keep my
        black pines on coconut chip bedding that I wet down frequently. There are 
        many fans in my snake room so the bedding doesn't sit in stagnant air and 
        get moldy. 
        I've found that the black pines like to burrow in the substrate a lot but 
        they do come out and roam the cage. So I have the room lighting on for 12 hours
        and off for 12 hours to mimic their wild environment.
      </BodyText>

      <BodyText>
        <h3>Feeding</h3>
        Like most captive snakes, black pines do very well on a diet of mice and 
        rats. I feed my female a medium rat and my male a small rat according to 
        the schedule in the chart - weekly for the female for most of the year, bi-weekly
        for the male for most of the year.
      </BodyText>

      <BodyText>
        <h3>Breeding</h3>
        <p>
          I breed my pair although as I said before they can't be sold across state 
          lines without a federal permit, so I'm mindful that the customer pool for 
          babies is limited to the state of Utah.
        </p>
        <p>
          But they are bred similar to the 
          way most other North American colubrids are bred. They get their last food at 
          the end of October and then during the month of November I slowly bring 
          their enclosure temperature down to the low 50s F. By December, they are 
          in a space where the lights are off 24h a day and the temperatures stay 
          in the low 50s. I have a water bowl in each enclosure and I replace the 
          water every 2 weeks during this cooling period. Then, in February I start increasing
          their daytime temperature and increasing the period of light for them until
          they are back at their previous temperatures. I should point out that during
          this cool-down period they have access to water but I don't make the substrate 
          very damp at all. I keep their humidity level a bit lower than usual because 
          I worry about the excess humidity and cold making them sick.
        </p>
        <p>
          Once they are back on their standard temperatures at the beginning of March, 
          the female starts getting
          her food first, but I hold off on the male for a few weeks. They are also 
          paired together at the beginning of March but I separate them for a couple 
          of days whenever one or both of them are being fed.
        </p>
        <p>
          I have only bred them a couple of years so far but each time the female has 
          had a pre-lay shed on the same day, 4/30. Then on 5/10 she has laid eggs. 
          The clutches have been 5 eggs each time, although the first year none of 
          the eggs made it to hatching. I did not candle the eggs when they were laid 
          so I don't know if they were viable or not. But the second year 
          there were 3 babies born after a 72-day incubation period at 80F.
        </p>
      </BodyText>

      <PageGallery slug="black-pinesnakes" maxWidth="900" />
    </ContentBox>
  );
};

export default BlackPinesnake;
